import { buildPublicError, type ApiErrorPayload, type PublicErrorCode, type PublicErrorLocale } from "./errors";
import type { TaskResult } from "./tasks";

export const PUBLIC_ERROR_REFERENCE_HEADERS = [
  { name: "Authorization", direction: "request" },
  { name: "Content-Type", direction: "request" },
  { name: "Accept-Language", direction: "request" },
  { name: "x-nexra-client-request-id", direction: "request" },
  { name: "Idempotency-Key", direction: "request" },
  { name: "x-request-id", direction: "response" },
  { name: "Content-Language", direction: "response" },
  { name: "x-nexra-error-code", direction: "response" },
] as const;

export const PUBLIC_ERROR_REFERENCE_CODES = [
  "content_policy_violation",
  "rate_limited",
  "invalid_request",
  "asset_too_large",
  "upstream_timeout",
  "upstream_unavailable",
  "upstream_busy",
  "execution_outcome_unknown",
  "stream_interrupted",
  "task_execution_timeout",
] as const satisfies readonly PublicErrorCode[];

/** HTTP outcomes are independent of public error classification and Task status. */
export const PUBLIC_ERROR_REFERENCE_HTTP_STATUSES = [
  200, 202, 400, 401, 402, 403, 404, 408, 409, 413, 422, 429, 500, 502, 503, 504,
] as const;

export const PUBLIC_ERROR_REFERENCE_FIELDS = [
  "nexraHttpStatus",
  "upstreamHttpStatus",
  "nexraRequestId",
  "upstreamRequestId",
  "nexraError",
  "nexraError.code",
  "nexraError.message",
  "nexraError.param",
  "upstreamError",
  "upstreamErrorMessage",
  "upstreamRedacted",
  "upstreamTruncated",
  "upstreamUnavailableReason",
  "upstreamFormat",
  "upstreamEvent",
] as const;

/** Illustrative provider response, not evidence from a live model invocation. */
export function publicErrorReferenceExamples(locale: PublicErrorLocale = "en", modelId = "example/model") {
  const nativeFailure = { error: {
    code: "moderation_blocked",
    message: "Your request was rejected by the safety system.",
    type: "image_generation_user_error",
  } };
  return {
    httpFailure: buildPublicError({ code: "idempotency_key_required" }, locale, "nexra-request-id", 400) satisfies ApiErrorPayload,
    taskFailure: {
      id: "task-example",
      model: modelId,
      status: "failed" as const,
      ...buildPublicError({ code: "content_policy_violation", upstream: {
        upstreamHttpStatus: 400,
        upstreamRequestId: "provider-request-id",
        body: nativeFailure,
      } }, locale, "nexra-create-request-id", 202),
    } satisfies TaskResult,
    nativeFailure,
  };
}
